import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import {
    BonusStatusEnum,
    BonusTypeEnum,
    DEFAULT_ACCOUNT_INDEX,
    PAGE,
    REGEX,
    TRADING_TYPE,
    WALLET_ACCOUNT_TYPES,
} from 'src/common/constants';
import { currencyFormatter } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Select,
    SelectItem,
    Input,
    Alert,
} from 'src/components/core';
import { Cross, TransferCircled } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    fetchAvailableAssets as fetchAvailableDerivativeAssets,
    showNotification as showDerivativeNotification,
} from 'src/setup/redux/slices/derivativesSlice';
import {
    fetchTransferAvailable,
    fetchTransferAvailableMargin,
    transferWalletBalance,
} from 'src/setup/redux/slices/globalSlice';
import {
    fetchAvailableAssets,
    showNotification as showSpotNotification,
} from 'src/setup/redux/slices/spotSlice';
import { getUserAssets } from 'src/setup/redux/slices/userSlice';
import { ToastBody, UserWallet } from 'src/types';

interface TransferModalProps {
    open: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography, breakpoints } }) => `
        .modal-paper {
            @media (min-width: ${breakpoints.md}) {
                width: 552px !important;
            }
        }
        .modal-title {
            padding: 20px 24px;
            > div {
                font-size: ${typography.size.desktop.heading.h6};
                line-height: calc(${typography.lineHeight[7]} - 1px);
                font-weight: ${typography.weight.medium};
            }
        }
        .modal-content {
            padding: 12px 24px 21px;
            & > div:not(:first-of-type) {
                margin-top: 16px;
            }
            .amount-input {
                color: ${colors.exchange.buy};
                margin-bottom: 8px;
                padding: 0 12px 0 0;
                input {
                    font-size: ${typography.size.desktop.paragraph.p2};
                    line-height: calc(${typography.lineHeight[3]} - 0.5px);
                    padding: 11px 12px;
                }
                &.input-element-error {
                    border-color: ${colors.border.light};    
                }
            }
            & .select {
                & > div:nth-of-type(1) {
                    & > span {
                        font-size: ${typography.size.desktop.paragraph.p2};
                        line-height: calc(${typography.lineHeight[3]} - 0.5px);
                    }
                    & > svg {
                        width: 19px;
                        height: 19px;
                    }
                }
            }
            & > .dropdown-row {
                @media (max-width: 425px) {
                    display: block;
                    & > div {
                        &:nth-of-type(2) {
                            justify-content: center;
                            margin: 12px 0;
                        }
                    }
                }
            }
        }
        .modal-actions {
            padding: 24px 24px 30px;
            gap: 16px;
            button {
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                padding: 10px 52px;
            }
            @media (max-width: 425px) {
                justify-content: center;
                button {
                    padding: 10px 32px;
                    width: 100%;
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        color: ${colors.text.tertiary};
        line-height: calc(${typography.lineHeight[3]} - 0.5px);
        margin-bottom: 8px;
    `}
`;

const TransferableText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.tertiary};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        margin-bottom: 8px;
        text-align: end;
        span {
            color: ${colors.exchange.buy};
            @media (max-width: 375px) {
                display: block;
            }
        }
    `}
`;

const TitleText = styled.div`
    display: inline-block;
`;
const StyledSpan = styled.span`
    cursor: pointer;
    white-space: nowrap;
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p2};
        line-height: calc(${typography.lineHeight[3]} - 0.5px);
    `}
`;

const TransferModal: FC<TransferModalProps> = ({ open, handleClose }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const tradeType = location.pathname.includes(PAGE.CONTRACT)
        ? TRADING_TYPE.DERIVATIVES
        : location.pathname.includes(PAGE.SPOT)
        ? TRADING_TYPE.SPOT
        : '';
    const { colors, typography } = useTheme();
    const [sourceAccount, setSourceAccount] = useState(
        tradeType === TRADING_TYPE.DERIVATIVES
            ? WALLET_ACCOUNT_TYPES.GENERAL_ACCOUNT
            : WALLET_ACCOUNT_TYPES.PERPETUALS_ACCOUNT,
    );
    const [targetAccount, setTargetAccount] = useState(
        tradeType === TRADING_TYPE.DERIVATIVES
            ? WALLET_ACCOUNT_TYPES.PERPETUALS_ACCOUNT
            : WALLET_ACCOUNT_TYPES.GENERAL_ACCOUNT,
    );
    const [filteredTokenIds, setFilteredTokenIds] = useState([] as string[]);
    const [sourceWallet, setSourceWallet] = useState({} as UserWallet);
    const [targetWallet, setTargetWallet] = useState({} as UserWallet);
    const [tokenId, setTokenId] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [transferError, setTransferError] = useState('');
    const [parsedTransferAbleAmount, setParsedTransferAbleAmount] = useState('');
    const { transferAbleAmount, coinTokens } = useAppSelector((state) => state.global);
    const transferWalletBalanceLoading = useAppSelector(
        (state) => state.global.loading.transferWalletBalance,
    );
    const { userWallets, userCurrentFutureTradeAccountIndex } = useAppSelector(
        (state) => state.global,
    );
    const tokens = useAppSelector((state) => state.global.configV2.token);
    const { userBonus } = useAppSelector((state) => state.user);

    const dispatch = useAppDispatch();

    const switchAccountClicked = () => {
        if (targetAccount !== sourceAccount) {
            setSourceAccount(targetAccount);
            setTargetAccount(sourceAccount);
            setTransferError('');
        }
    };

    useEffect(() => {
        let sourceTokenIds: string[] = [];
        let targetTokenIds: string[] = [];
        let tokenIds: string[] = [];
        let currentTokenId = '';
        const sourceWalletAcc =
            userWallets?.find((wallet) => wallet.accountType === sourceAccount) ||
            ({} as UserWallet);
        const targetWalletAcc =
            userWallets?.find((wallet) => wallet.accountType === targetAccount) ||
            ({} as UserWallet);
        setSourceWallet(sourceWalletAcc);
        setTargetWallet(targetWalletAcc);
        setTransferError('');

        const getFilteredIds = (wallet: UserWallet | undefined, accountType: number): string[] => {
            let ids: string[] = [];
            if (wallet) {
                switch (accountType) {
                    case WALLET_ACCOUNT_TYPES.GENERAL_ACCOUNT:
                        ids = Object.keys(tokens);
                        break;
                    case WALLET_ACCOUNT_TYPES.PERPETUALS_ACCOUNT:
                        ids = coinTokens.futuresCoinToken;
                        break;
                    case WALLET_ACCOUNT_TYPES.MARGIN_ACCOUNT:
                        ids = coinTokens.marginCoinToken;
                        break;
                }
            }
            return ids;
        };
        sourceTokenIds = getFilteredIds(sourceWalletAcc, sourceAccount);
        targetTokenIds = getFilteredIds(targetWalletAcc, targetAccount);
        tokenIds = sourceTokenIds?.filter((id) => targetTokenIds?.includes(id));
        setFilteredTokenIds(tokenIds);
        if (tokenIds?.length > 0 && sourceAccount !== targetAccount) {
            currentTokenId = tokenIds[0];
            setTokenId(tokenIds[0]);
        } else if (sourceAccount === targetAccount) {
            currentTokenId = tokenIds.find((id) => id === 'USDT') || '';
            setTokenId(currentTokenId);
        }

        if (sourceWallet && currentTokenId !== '') {
            if (sourceAccount === WALLET_ACCOUNT_TYPES.MARGIN_ACCOUNT) {
                dispatch(fetchTransferAvailableMargin({ token_id: currentTokenId }));
            } else {
                const sourceWallet = userWallets?.find(
                    (wallet) => wallet.accountType === sourceAccount,
                );

                if (sourceWallet) {
                    dispatch(
                        fetchTransferAvailable({
                            account_type: sourceWallet.accountType,
                            account_index: sourceWallet.accountIndex,
                            token_id: currentTokenId,
                            account_id: sourceWallet.accountId,
                            account_name: sourceWallet.accountName,
                        }),
                    );
                }
            }
        }
        setTransferAmount('');
        setParsedTransferAbleAmount('0.00000000');
    }, [
        dispatch,
        sourceAccount,
        targetAccount,
        userWallets,
        coinTokens,
        tokens,
        sourceWallet,
        open,
    ]);

    useEffect(() => {
        setParsedTransferAbleAmount(transferAbleAmount.toString());
    }, [transferAbleAmount]);

    const formatAmount = () => {
        if (Number(parsedTransferAbleAmount) != 0) {
            const amountArr = parsedTransferAbleAmount.toString().split('.');
            let amount = '';
            if (amountArr.length > 1) {
                amount = `${amountArr[0]}.${amountArr[1].slice(0, 8)}`;
            } else {
                amount = amountArr[0];
            }
            return Number(amount);
        }
    };

    const onAmountChange = (val: string) => {
        setTransferError('');
        if (val !== '') {
            if (REGEX.POSITIVE_DECIMAL.test(`${val}`)) {
                setTransferAmount(`${val}`);
            } else {
                setTransferError('Please enter only numbers');
            }
        } else {
            setTransferAmount(`${val}`);
        }
    };

    const handleAllAssetClicked = () => {
        const amount = formatAmount();
        setTransferError('');
        if (amount && amount !== 0) {
            setTransferAmount(amount.toString());
        }
    };

    const handleConfirm = () => {
        const amount = parseFloat(transferAmount);
        const transferAble = parseFloat(parsedTransferAbleAmount);
        if (Number(transferAmount) <= 0) {
            setTransferError('Amount should be between 0 and available balance');
        } else if (amount > transferAble) {
            setTransferError('Insufficient available balance. Please try again');
        } else {
            const notificationBody = {
                title: 'transfer_successful',
                type: 'success',
            } as ToastBody;
            dispatch(
                transferWalletBalance({
                    token_id: tokenId,
                    amount: amount,
                    from_account_type: sourceAccount,
                    from_account_index: sourceWallet.accountIndex,
                    from_account_id: sourceWallet.accountId,
                    to_account_type: targetAccount,
                    to_account_index: targetWallet.accountIndex,
                    to_account_id: targetWallet.accountId,
                }),
            ).then(() => {
                switch (tradeType) {
                    case TRADING_TYPE.DERIVATIVES:
                        dispatch(
                            fetchAvailableDerivativeAssets({
                                account_index: userCurrentFutureTradeAccountIndex,
                            }),
                        );
                        dispatch(showDerivativeNotification(notificationBody));
                        dispatch(getUserAssets({ unit: 'USDT' }));
                        break;
                    case TRADING_TYPE.SPOT:
                        dispatch(fetchAvailableAssets());
                        dispatch(showSpotNotification(notificationBody));
                        break;
                }
            });
            handleClose();
        }
    };

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('transfer')}</TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                {userBonus?.type === BonusTypeEnum.FUTURES_ACCOUNT &&
                    userBonus?.status === BonusStatusEnum.ACTIVE && (
                        <Alert type="warning" content={t('futures_bonus_disclaimer')} />
                    )}
                <FlexContainer styles={{ gap: 24, alignItems: 'end' }} className={'dropdown-row'}>
                    <FlexContainer styles={{ flexDirection: 'column', width: '100%' }}>
                        <LabelText style={{ width: '100%' }}>{t('from')}</LabelText>
                        <Select
                            arrow={false}
                            value={sourceAccount}
                            onChange={setSourceAccount}
                            styles={{ width: '100%' }}
                            headStyles={{
                                height: 40,
                                fontSize: typography.size.desktop.paragraph.p2,
                                lineHeight: `calc(${typography.lineHeight[3]} - 0.5px)`,
                                padding: '11px 12px',
                            }}
                        >
                            {userWallets?.map((wallet: UserWallet, index) => {
                                return (
                                    wallet.accountIndex === DEFAULT_ACCOUNT_INDEX && (
                                        <SelectItem key={index} value={wallet.accountType}>
                                            {t(wallet.accountName.toLowerCase())}
                                        </SelectItem>
                                    )
                                );
                            })}
                        </Select>
                    </FlexContainer>
                    <FlexContainer>
                        <TransferCircled
                            fill={colors.background.primary}
                            stroke={colors.primary}
                            style={{ alignSelf: 'flex-end', cursor: 'pointer' }}
                            onClick={switchAccountClicked}
                        />
                    </FlexContainer>
                    <FlexContainer styles={{ flexDirection: 'column', width: '100%' }}>
                        <LabelText style={{ width: '100%' }}>{t('to')}</LabelText>
                        <Select
                            arrow={false}
                            value={targetAccount}
                            onChange={setTargetAccount}
                            styles={{ width: '100%' }}
                            headStyles={{
                                height: 40,
                                fontSize: typography.size.desktop.paragraph.p2,
                                lineHeight: `calc(${typography.lineHeight[3]} - 0.5px)`,
                                padding: '11px 12px',
                            }}
                        >
                            {userWallets?.map((wallet: UserWallet, index) => {
                                return (
                                    wallet.accountIndex === DEFAULT_ACCOUNT_INDEX && (
                                        <SelectItem key={index} value={wallet.accountType}>
                                            {t(wallet.accountName.toLowerCase())}
                                        </SelectItem>
                                    )
                                );
                            })}
                        </Select>
                    </FlexContainer>
                </FlexContainer>
                <FlexContainer
                    styles={{ flexDirection: 'column', width: '100%', alignItems: 'start' }}
                >
                    <LabelText>{t('coin')}</LabelText>
                    <Select
                        value={tokenId}
                        onChange={setTokenId}
                        styles={{ width: '100%' }}
                        disabled={sourceAccount === targetAccount}
                        headStyles={{
                            height: 40,
                            fontSize: typography.size.desktop.paragraph.p2,
                            lineHeight: `calc(${typography.lineHeight[3]} - 0.5px)`,
                            padding: '11px 12px',
                        }}
                    >
                        {(filteredTokenIds || []).map((token: string, index) => (
                            <SelectItem key={index} value={token}>
                                {token}
                            </SelectItem>
                        ))}
                    </Select>
                </FlexContainer>
                <FlexContainer
                    styles={{ flexDirection: 'column', width: '100%', alignItems: 'start' }}
                >
                    <FlexContainer styles={{ justifyContent: 'space-between', width: '100%' }}>
                        <LabelText>{t('amount')}</LabelText>
                        <TransferableText>
                            {t('transferable_amount')}&nbsp;
                            <span>
                                {currencyFormatter(formatAmount() || 0.0, 8)} {tokenId}
                            </span>
                        </TransferableText>
                    </FlexContainer>
                    <Input
                        name={'amount'}
                        hasTooltip={false}
                        fullWidth
                        value={transferAmount}
                        className={'amount-input'}
                        error={transferError}
                        onChange={(val) => onAmountChange(val.toString())}
                        rightElement={
                            <StyledSpan onClick={handleAllAssetClicked}>{t('all')}</StyledSpan>
                        }
                    />
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button variant="outlined" onClick={handleClose}>
                    {t('cancel')}
                </Button>
                <Button
                    loading={transferWalletBalanceLoading}
                    onClick={handleConfirm}
                    disabled={sourceWallet === targetWallet}
                >
                    {t('confirm')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { TransferModal };
